import type { MapCanvasValue } from "@gettactic/map-builder/src/components/map/contexts/MapCanvasContext/MapCanvasContext.types";
import type {
  Shape,
  SpotShape,
  SpotShape001
} from "@gettactic/map-builder/src/types";
import { migrateWallsToPath } from "@gettactic/map-builder/src/utils/wallPathMigration";
import { bakeWallRotations } from "@gettactic/map-builder/src/utils/wallRotationMigration";
import { MAP_BUILDER_VERSION } from "@gettactic/map-builder/src/version";
import type { ShapeById } from "../types";

/** Everything in a saved map besides the shapes. Round-tripped untouched. */
export type DocumentMeta = Omit<MapCanvasValue, "shapes">;

export interface EditorDocument {
  shapes: ShapeById;
  /** Z-order, bottom to top — the saved `shapes[]` order. */
  order: string[];
  meta: DocumentMeta;
}

export const DEFAULT_META: DocumentMeta = {
  gridSize: 20,
  editingZoneWidth: 3000,
  editingZoneHeight: 2000,
  spotLabelPattern: [{ type: "numbers", depth: 999 }],
  lastSpotLabel: null
};

/**
 * Same load-time migrations as the Konva canvas provider, so a map opened
 * here is the document the other editor would have saved: version-less
 * spots (`rows[]`) flattened, legacy wall segments joined into wall paths,
 * wall rotations baked into their endpoints.
 */
export const migrateLoadedShapes = (
  shapes: Shape[],
  version: string | undefined
): Shape[] => {
  const flattened = version
    ? shapes
    : shapes.map((shape) => {
        if (shape.type !== "spot") return shape;
        const legacy = shape as unknown as SpotShape001;
        const first = legacy.rows?.[0]?.items?.[0];
        if (!first) return shape;
        return {
          ...legacy,
          ...first,
          rows: undefined,
          label: first.textProps?.text || ""
        } as unknown as SpotShape;
      });
  return bakeWallRotations(migrateWallsToPath(flattened));
};

export const fromMapValue = (value?: MapCanvasValue): EditorDocument => {
  // Duplicate ids exist in old maps; the last one wins, which is what the
  // Konva editor draws on top.
  const { shapes, order } = documentFromShapes(
    migrateLoadedShapes(value?.shapes ?? [], value?.version)
  );
  const { shapes: _shapes, ...meta } = value ?? { shapes: [] };
  const document: EditorDocument = {
    shapes,
    order,
    meta: { ...DEFAULT_META, ...meta }
  };
  if (!value?.version) {
    // Version-less maps carry no usable label pattern (the provider
    // ignores those fields too).
    document.meta.spotLabelPattern = DEFAULT_META.spotLabelPattern;
    document.meta.lastSpotLabel = DEFAULT_META.lastSpotLabel;
  }
  return document;
};

export const toMapValue = (document: EditorDocument): MapCanvasValue => ({
  ...document.meta,
  version: MAP_BUILDER_VERSION,
  lastUpdate: new Date().toISOString(),
  shapes: orderedShapes(document)
});

/** Builds the map form of a saved `shapes[]` array (last duplicate id wins). */
export const documentFromShapes = (
  list: Shape[]
): Pick<EditorDocument, "shapes" | "order"> => {
  const shapes: ShapeById = {};
  const order: string[] = [];
  for (const shape of list) {
    if (!(shape.id in shapes)) order.push(shape.id);
    shapes[shape.id] = shape;
  }
  return { shapes, order };
};

export const orderedShapes = (
  document: Pick<EditorDocument, "shapes" | "order">
): Shape[] => {
  const result: Shape[] = [];
  for (const id of document.order) {
    const shape = document.shapes[id];
    if (shape) result.push(shape);
  }
  return result;
};
